/**
 * Theme Provider
 *
 * Self-contained React context provider for theme management.
 * Persists preferences to localStorage. Accepts optional callbacks
 * so consuming apps can sync with their own state (e.g., Zustand).
 */

import React, { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { COLOR_PRESETS, type ColorPreset, DEFAULT_PRESET, getPresetById } from '../tokens/colors';
import { DEFAULT_FONT_FAMILY, FONT_FAMILIES, type FontFamily, getFontFamilyById } from '../tokens/typography';
import type { DensityLevel } from '../tokens/spacing';
import { applyTheme, type ThemeConfig } from './theme-generator';
import { loadFont } from './font-loader';

// ============================================================================
// TYPES
// ============================================================================

export interface Theme {
  colorPreset: ColorPreset;
  mode: 'light' | 'dark' | 'system';
  fontFamily: FontFamily;
  /**
   * Phase 4.4b-1 — optional separate display/heading font. When
   * undefined, headings inherit the body font (back-compat with
   * pre-4.4b-1 themes). The Branding step in onboarding will set
   * this from a picker; auth screens land it as a static prop.
   */
  fontDisplay?: FontFamily;
  density: DensityLevel;
}

export interface ThemeContextValue extends Theme {
  setColorPreset: (presetId: string) => void;
  setMode: (mode: 'light' | 'dark' | 'system') => void;
  setFontFamily: (fontId: string) => void;
  /** Pass `null` to clear the override and have headings inherit the body font. */
  setFontDisplay: (fontId: string | null) => void;
  /**
   * Phase 4.4c-1 re-review fix #1 — atomic body+display setter. Use
   * this when picking an approved font PAIRING; calling
   * `setFontFamily` and `setFontDisplay` separately persists in two
   * passes, and the second pass reads the FIRST setter's pre-update
   * closure — yielding a stored shape with the OLD body font and the
   * NEW display font (or vice versa). This setter writes both
   * states + persists once with both new values atomic.
   *
   * Pass `fontDisplayId: null` to clear the display override (sans-only
   * pairings).
   */
  setTypography: (input: { fontFamilyId: string; fontDisplayId: string | null }) => void;
  setDensity: (density: DensityLevel) => void;
  availablePresets: ColorPreset[];
  isDarkMode: boolean;
}

// ============================================================================
// STORAGE HELPERS
// ============================================================================

const STORAGE_KEY = 'invento-theme';

interface StoredTheme {
  colorPresetId: string;
  mode: 'light' | 'dark' | 'system';
  fontFamilyId: string;
  /**
   * Phase 4.4b-1 — optional. Older stored payloads omit this field;
   * the loader treats absence as "inherit body" so existing users
   * don't see a heading-font shift on the next app load.
   */
  fontDisplayId?: string | null;
  density: DensityLevel;
}

function loadStoredTheme(): StoredTheme | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveStoredTheme(theme: StoredTheme): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(theme));
  } catch {
    // Ignore storage errors
  }
}

// ============================================================================
// CONTEXT
// ============================================================================

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// ============================================================================
// PROVIDER PROPS
// ============================================================================

export interface ThemeProviderProps {
  children: ReactNode;
  initialTheme?: Partial<Theme>;
  /** Called whenever any theme property changes (for external store sync) */
  onThemeChange?: (theme: StoredTheme) => void;
}

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, initialTheme, onThemeChange }) => {
  const stored = loadStoredTheme();

  const [colorPreset, setColorPresetState] = useState<ColorPreset>(() => {
    const fromStorage = stored?.colorPresetId ? getPresetById(stored.colorPresetId) : null;
    return fromStorage || initialTheme?.colorPreset || DEFAULT_PRESET;
  });
  const [mode, setModeState] = useState<'light' | 'dark' | 'system'>(
    stored?.mode || initialTheme?.mode || 'system'
  );
  const [fontFamily, setFontFamilyState] = useState<FontFamily>(
    (stored?.fontFamilyId ? getFontFamilyById(stored.fontFamilyId) : null) ||
      initialTheme?.fontFamily ||
      DEFAULT_FONT_FAMILY
  );
  const [fontDisplay, setFontDisplayState] = useState<FontFamily | undefined>(
    (stored?.fontDisplayId ? getFontFamilyById(stored.fontDisplayId) ?? undefined : undefined) ||
      initialTheme?.fontDisplay
  );
  const [density, setDensityState] = useState<DensityLevel>(
    stored?.density || initialTheme?.density || 'comfortable'
  );

  // Detect system preference
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false
  );

  const isDarkMode = mode === 'dark' || (mode === 'system' && systemPrefersDark);

  // Persist + notify helper
  const persist = useCallback(
    (update: Partial<StoredTheme>) => {
      // `fontDisplayId` is `string | null | undefined`: undefined means
      // "no change, fall back to current state"; null means "explicit
      // clear" (preserved verbatim so the persisted snapshot can record
      // intent — the architect-pinned re-review fix).
      const fontDisplayIdResolved =
        update.fontDisplayId === undefined ? fontDisplay?.id : update.fontDisplayId;
      const full: StoredTheme = {
        colorPresetId: update.colorPresetId ?? colorPreset.id,
        mode: update.mode ?? mode,
        fontFamilyId: update.fontFamilyId ?? fontFamily.id,
        fontDisplayId: fontDisplayIdResolved,
        density: update.density ?? density,
      };
      saveStoredTheme(full);
      onThemeChange?.(full);
    },
    [colorPreset.id, mode, fontFamily.id, fontDisplay?.id, density, onThemeChange]
  );

  // Listen for system preference changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Apply theme whenever it changes
  useEffect(() => {
    const config: ThemeConfig = {
      colorPreset,
      mode: isDarkMode ? 'dark' : 'light',
      fontFamily: `${fontFamily.family}, ${fontFamily.fallback}`,
      fontDisplay: fontDisplay
        ? `${fontDisplay.family}, ${fontDisplay.fallback}`
        : undefined,
      density,
    };
    applyTheme(config);
  }, [colorPreset, isDarkMode, fontFamily, fontDisplay, density]);

  // Load initial font
  useEffect(() => {
    loadFont(fontFamily.id).catch(console.error);
  }, [fontFamily.id]);

  const setColorPreset = useCallback(
    (presetId: string) => {
      const preset = COLOR_PRESETS.find((p) => p.id === presetId);
      if (preset) {
        setColorPresetState(preset);
        persist({ colorPresetId: presetId });
      }
    },
    [persist]
  );

  const setMode = useCallback(
    (newMode: 'light' | 'dark' | 'system') => {
      setModeState(newMode);
      persist({ mode: newMode });
    },
    [persist]
  );

  const setFontFamily = useCallback(
    (fontId: string) => {
      const font = FONT_FAMILIES.find((f: FontFamily) => f.id === fontId);
      if (font) {
        setFontFamilyState(font);
        persist({ fontFamilyId: fontId });
        loadFont(fontId).catch(console.error);
      }
    },
    [persist]
  );

  const setFontDisplay = useCallback(
    (fontId: string | null) => {
      if (fontId === null) {
        setFontDisplayState(undefined);
        persist({ fontDisplayId: null });
        return;
      }
      const font = FONT_FAMILIES.find((f: FontFamily) => f.id === fontId);
      if (font) {
        setFontDisplayState(font);
        persist({ fontDisplayId: fontId });
        loadFont(fontId).catch(console.error);
      }
    },
    [persist]
  );

  const setDensity = useCallback(
    (newDensity: DensityLevel) => {
      setDensityState(newDensity);
      persist({ density: newDensity });
    },
    [persist]
  );

  const setTypography = useCallback(
    (input: { fontFamilyId: string; fontDisplayId: string | null }) => {
      const body = FONT_FAMILIES.find((f: FontFamily) => f.id === input.fontFamilyId);
      if (!body) return;
      const display =
        input.fontDisplayId === null
          ? undefined
          : FONT_FAMILIES.find((f: FontFamily) => f.id === input.fontDisplayId);
      // Update both states + persist atomically with both new values.
      // The two-setter sequence races persist() against React's
      // pending-state closure; this single call sidesteps that.
      setFontFamilyState(body);
      setFontDisplayState(display);
      persist({
        fontFamilyId: input.fontFamilyId,
        fontDisplayId: input.fontDisplayId,
      });
      // Kick off Google Fonts loads for whichever fonts are now active.
      loadFont(body.id).catch(console.error);
      if (display) loadFont(display.id).catch(console.error);
    },
    [persist],
  );

  const value: ThemeContextValue = {
    colorPreset,
    mode,
    fontFamily,
    fontDisplay,
    density,
    setColorPreset,
    setMode,
    setFontFamily,
    setFontDisplay,
    setTypography,
    setDensity,
    availablePresets: COLOR_PRESETS,
    isDarkMode,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// ============================================================================
// HOOK
// ============================================================================

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
